import { HTMLProps, memo, ReactNode, useMemo, useState } from 'react';
import styled from 'styled-components';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { getDOMProps } from 'app/shared/utils/react';
import { alpha } from '@mui/system/colorManipulator';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21HelpTooltip } from 'app/shared/u21-ui/components/display/U21HelpTooltip';

export interface U21TreeNode {
  id: string;
  label: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  help?: ReactNode;
  children?: U21TreeNode[];
}

export interface U21TreeNavigatorProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'data' | 'onSelect'
> {
  data: U21TreeNode[];
  collapsible?: boolean;
  defaultExpanded?: string[];
  defaultSelected?: string;
  onSelect?: (nodeId: string) => void;
}

const EmptyIcon = () => null;

const getTreeItems = (
  nodes: U21TreeNode[] | undefined,
): { treeNodes: ReactNode[] | undefined; treeNodeIds: string[] } => {
  if (!nodes) return { treeNodes: undefined, treeNodeIds: [] };

  const treeNodes: ReactNode[] = [];
  const treeNodeIds: string[] = [];

  nodes.forEach((node) => {
    const { treeNodes: childTreeNodes, treeNodeIds: childTreeNodeIds } =
      getTreeItems(node.children);

    treeNodeIds.push(...childTreeNodeIds, node.id);

    treeNodes.push(
      <StyledTreeItem
        key={node.id}
        itemId={node.id}
        label={
          <LabelContainer horizontal>
            <U21Spacer horizontal>
              {node.startIcon && (
                <IconContainer>{node.startIcon}</IconContainer>
              )}
              <U21Typography variant="body2">{node.label}</U21Typography>
              {node.help && <U21HelpTooltip help={node.help} />}
            </U21Spacer>
            {node.endIcon && <IconContainer>{node.endIcon}</IconContainer>}
          </LabelContainer>
        }
      >
        {childTreeNodes}
      </StyledTreeItem>,
    );
  });

  return { treeNodes, treeNodeIds };
};

const U21TreeNavigatorInternal = ({
  data,
  collapsible = false,
  defaultExpanded = [],
  defaultSelected,
  onSelect,
  ...rest
}: U21TreeNavigatorProps) => {
  const { treeNodes, treeNodeIds } = useMemo(() => getTreeItems(data), [data]);

  const [expanded, setExpanded] = useState<string[]>(defaultExpanded);

  return (
    <StyledTreeView
      {...getDOMProps(rest)}
      defaultSelectedItems={defaultSelected}
      expandedItems={collapsible ? expanded : treeNodeIds}
      onExpandedItemsChange={(_, nodeIds: string[]) => {
        if (collapsible) {
          setExpanded(nodeIds);
        }
      }}
      onSelectedItemsChange={(_, nodeId: string) => {
        if (onSelect) {
          onSelect(nodeId);
        }
      }}
      slots={{
        collapseIcon: collapsible ? undefined : EmptyIcon,
        expandIcon: collapsible ? undefined : EmptyIcon,
      }}
    >
      {treeNodes}
    </StyledTreeView>
  );
};

export const U21TreeNavigator = memo(U21TreeNavigatorInternal);

const StyledTreeView = styled(SimpleTreeView)`
  flex-grow: 1;
  max-width: 400px;
`;

const StyledTreeItem = styled(TreeItem)`
  .MuiTreeItem-content {
    padding: 8px;

    &:hover {
      background-color: ${({ theme }) => theme.palette.action.hover};
    }

    &.Mui-selected {
      background-color: ${({ theme }) =>
        alpha(
          theme.palette.primary.main,
          theme.palette.mode === 'light' ? 0.08 : 0.16,
        )};

      &:hover {
        background-color: ${({ theme }) =>
          alpha(
            theme.palette.primary.main,
            theme.palette.mode === 'light' ? 0.16 : 0.24,
          )};
      }
    }
  }
`;

const LabelContainer = styled(U21Spacer)`
  justify-content: space-between;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
`;
